'use client';

import { clsxm } from '@vs-shop/utils';
import type { ClassValue } from 'clsx';

import {
  CartDrawerActions,
  CartDrawerBack,
  CartDrawerForward,
} from '@vs-shop/commerce-ui/cartDrawer';
import { useIntl } from 'react-intl';

import { Url } from '../../../../route/router';
import { pendingCartLinesAtom } from '../../../../effect/atom/order/cartMutations';
import { useAtomValue } from '@effect/atom-react';
import {
  hasLiveCartWrite,
  hasUncertainCartWrite,
} from '@vs-shop/engine/module/cart/cartDrawerProjection';

type Props = {
  className?: ClassValue;
};

/**
 * The two controls at the foot of the drawer: the way back, which only exists
 * where the panel is the whole screen, and the way to the cart.
 */
export default function CartDrawerButtons({ className }: Props) {
  const intl = useIntl();
  const pendingLines = useAtomValue(pendingCartLinesAtom);
  const isCartWriteLive = hasLiveCartWrite(pendingLines);
  const isCartWriteUncertain = hasUncertainCartWrite(pendingLines);
  const statusId = 'cart-drawer-navigation-status';

  return (
    <>
      <CartDrawerActions className={clsxm(className)}>
        <CartDrawerBack className="flex flex-1 tab:hidden">
          {intl.formatMessage({
            id: 'component.server.page.cart.cartDrawerButtons.back',
            defaultMessage: 'Zpět',
          })}
        </CartDrawerBack>

        <CartDrawerForward
          {...(isCartWriteLive ? { disabled: true } : { render: <a href={Url.cart} /> })}
          role={isCartWriteLive ? undefined : 'link'}
          {...(isCartWriteLive || isCartWriteUncertain ? { 'aria-describedby': statusId } : {})}
        >
          {intl.formatMessage({
            id: 'component.server.page.cart.cartDrawerButtons.goToCart',
            defaultMessage: 'Přejít do košíku',
          })}
        </CartDrawerForward>
      </CartDrawerActions>
      {(isCartWriteLive || isCartWriteUncertain) && (
        <span
          id={statusId}
          role="status"
          aria-live="polite"
          className="sr-only"
          data-slot="cart-drawer-navigation-status"
        >
          {isCartWriteUncertain && !isCartWriteLive
            ? intl.formatMessage({
                id: 'cartDrawer.pendingLine.unresolved',
                defaultMessage: 'Změnu košíku se nepodařilo ověřit',
              })
            : intl.formatMessage({
                id: 'cartDrawer.navigation.waiting',
                defaultMessage: 'Čekáme na potvrzení změny košíku.',
              })}
        </span>
      )}
    </>
  );
}
